import * as i0 from '@angular/core';
import { TemplateRef, numberAttribute, Input, ViewChild, ViewEncapsulation, ChangeDetectionStrategy, Component, inject, ChangeDetectorRef, DestroyRef, booleanAttribute, ContentChildren, NgModule } from '@angular/core';
import { Subject, merge } from 'rxjs';
import { __esDecorate, __runInitializers } from 'tslib';
import { Directionality } from '@angular/cdk/bidi';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith, switchMap, auditTime, tap } from 'rxjs/operators';
import { WithConfig, NzConfigService } from 'ng-zorro-antd/core/config';
import { warn } from 'ng-zorro-antd/core/logger';
import * as i1 from 'ng-zorro-antd/core/outlet';
import { NzOutletModule } from 'ng-zorro-antd/core/outlet';
import { NzBreakpointService, NzBreakpointEnum, gridResponsiveMap } from 'ng-zorro-antd/core/services';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const _c0 = ["*"];
function NzDescriptionsItemComponent_ng_template_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function NzDescriptionsComponent_Conditional_0_Conditional_1_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.nzTitle);
  }
}
function NzDescriptionsComponent_Conditional_0_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵtemplate(1, NzDescriptionsComponent_Conditional_0_Conditional_1_ng_container_1_Template, 2, 1, "ng-container", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r0.nzTitle);
  }
}
function NzDescriptionsComponent_Conditional_0_Conditional_2_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.nzExtra);
  }
}
function NzDescriptionsComponent_Conditional_0_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 3);
    i0.ɵɵtemplate(1, NzDescriptionsComponent_Conditional_0_Conditional_2_ng_container_1_Template, 2, 1, "ng-container", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r0.nzExtra);
  }
}
function NzDescriptionsComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 0);
    i0.ɵɵconditionalCreate(1, NzDescriptionsComponent_Conditional_0_Conditional_1_Template, 2, 1, "div", 2);
    i0.ɵɵconditionalCreate(2, NzDescriptionsComponent_Conditional_0_Conditional_2_Template, 2, 1, "div", 3);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.nzTitle ? 1 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.nzExtra ? 2 : -1);
  }
}
function NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_0_ng_container_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const item_r2 = i0.ɵɵnextContext(2).$implicit;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", item_r2.title, " ");
  }
}
function NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_0_ng_template_5_Template(rf, ctx) {}
function NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "td", 6)(1, "div", 7)(2, "span", 8);
    i0.ɵɵtemplate(3, NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_0_ng_container_3_Template, 2, 1, "ng-container", 4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "span", 9);
    i0.ɵɵtemplate(5, NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_0_ng_template_5_Template, 0, 0, "ng-template", 10);
    i0.ɵɵelementEnd()()();
  }
  if (rf & 2) {
    const item_r2 = i0.ɵɵnextContext().$implicit;
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("colSpan", item_r2.span);
    i0.ɵɵadvance(2);
    i0.ɵɵclassProp("ant-descriptions-item-no-colon", !ctx_r0.nzColon);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", item_r2.title);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngTemplateOutlet", item_r2.content);
  }
}
function NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_1_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const item_r2 = i0.ɵɵnextContext(2).$implicit;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", item_r2.title, " ");
  }
}
function NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_1_ng_template_3_Template(rf, ctx) {}
function NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "td", 8);
    i0.ɵɵtemplate(1, NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_1_ng_container_1_Template, 2, 1, "ng-container", 4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(2, "td", 11);
    i0.ɵɵtemplate(3, NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_1_ng_template_3_Template, 0, 0, "ng-template", 10);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const item_r2 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", item_r2.title);
    i0.ɵɵadvance();
    i0.ɵɵproperty("colSpan", item_r2.span * 2 - 1);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", item_r2.content);
  }
}
function NzDescriptionsComponent_Conditional_4_For_1_For_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_0_Template, 6, 5, "td", 6)(1, NzDescriptionsComponent_Conditional_4_For_1_For_2_Conditional_1_Template, 4, 3);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵconditional(!ctx_r0.nzBordered ? 0 : 1);
  }
}
function NzDescriptionsComponent_Conditional_4_For_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "tr", 5);
    i0.ɵɵrepeaterCreate(1, NzDescriptionsComponent_Conditional_4_For_1_For_2_Template, 2, 1, null, null, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const row_r3 = ctx.$implicit;
    i0.ɵɵadvance();
    i0.ɵɵrepeater(row_r3);
  }
}
function NzDescriptionsComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵrepeaterCreate(0, NzDescriptionsComponent_Conditional_4_For_1_Template, 3, 0, "tr", 5, i0.ɵɵrepeaterTrackByIdentity);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵrepeater(ctx_r0.itemMatrix);
  }
}
function NzDescriptionsComponent_Conditional_5_Conditional_0_For_1_For_2_ng_container_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const item_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", item_r4.title, " ");
  }
}
function NzDescriptionsComponent_Conditional_5_Conditional_0_For_1_For_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "td", 6)(1, "div", 7)(2, "span", 8);
    i0.ɵɵtemplate(3, NzDescriptionsComponent_Conditional_5_Conditional_0_For_1_For_2_ng_container_3_Template, 2, 1, "ng-container", 4);
    i0.ɵɵelementEnd()()();
  }
  if (rf & 2) {
    const item_r4 = ctx.$implicit;
    const ctx_r0 = i0.ɵɵnextContext(4);
    i0.ɵɵproperty("colSpan", item_r4.span);
    i0.ɵɵadvance(2);
    i0.ɵɵclassProp("ant-descriptions-item-no-colon", !ctx_r0.nzColon);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", item_r4.title);
  }
}
function NzDescriptionsComponent_Conditional_5_Conditional_0_For_1_For_5_ng_template_3_Template(rf, ctx) {}
function NzDescriptionsComponent_Conditional_5_Conditional_0_For_1_For_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "td", 6)(1, "div", 7)(2, "span", 9);
    i0.ɵɵtemplate(3, NzDescriptionsComponent_Conditional_5_Conditional_0_For_1_For_5_ng_template_3_Template, 0, 0, "ng-template", 10);
    i0.ɵɵelementEnd()()();
  }
  if (rf & 2) {
    const item_r5 = ctx.$implicit;
    i0.ɵɵproperty("colSpan", item_r5.span);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngTemplateOutlet", item_r5.content);
  }
}
function NzDescriptionsComponent_Conditional_5_Conditional_0_For_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "tr", 5);
    i0.ɵɵrepeaterCreate(1, NzDescriptionsComponent_Conditional_5_Conditional_0_For_1_For_2_Template, 4, 4, "td", 6, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "tr", 5);
    i0.ɵɵrepeaterCreate(4, NzDescriptionsComponent_Conditional_5_Conditional_0_For_1_For_5_Template, 4, 2, "td", 6, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const row_r6 = ctx.$implicit;
    i0.ɵɵadvance();
    i0.ɵɵrepeater(row_r6);
    i0.ɵɵadvance(3);
    i0.ɵɵrepeater(row_r6);
  }
}
function NzDescriptionsComponent_Conditional_5_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵrepeaterCreate(0, NzDescriptionsComponent_Conditional_5_Conditional_0_For_1_Template, 6, 0, null, null, i0.ɵɵrepeaterTrackByIdentity);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵrepeater(ctx_r0.itemMatrix);
  }
}
function NzDescriptionsComponent_Conditional_5_Conditional_1_For_1_For_2_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const item_r7 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", item_r7.title, " ");
  }
}
function NzDescriptionsComponent_Conditional_5_Conditional_1_For_1_For_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "td", 12);
    i0.ɵɵtemplate(1, NzDescriptionsComponent_Conditional_5_Conditional_1_For_1_For_2_ng_container_1_Template, 2, 1, "ng-container", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const item_r7 = ctx.$implicit;
    i0.ɵɵproperty("colSpan", item_r7.span);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", item_r7.title);
  }
}
function NzDescriptionsComponent_Conditional_5_Conditional_1_For_1_For_5_ng_template_1_Template(rf, ctx) {}
function NzDescriptionsComponent_Conditional_5_Conditional_1_For_1_For_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "td", 11);
    i0.ɵɵtemplate(1, NzDescriptionsComponent_Conditional_5_Conditional_1_For_1_For_5_ng_template_1_Template, 0, 0, "ng-template", 10);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const item_r8 = ctx.$implicit;
    i0.ɵɵproperty("colSpan", item_r8.span);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", item_r8.content);
  }
}
function NzDescriptionsComponent_Conditional_5_Conditional_1_For_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "tr", 5);
    i0.ɵɵrepeaterCreate(1, NzDescriptionsComponent_Conditional_5_Conditional_1_For_1_For_2_Template, 2, 2, "td", 12, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "tr", 5);
    i0.ɵɵrepeaterCreate(4, NzDescriptionsComponent_Conditional_5_Conditional_1_For_1_For_5_Template, 2, 2, "td", 11, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const row_r9 = ctx.$implicit;
    i0.ɵɵadvance();
    i0.ɵɵrepeater(row_r9);
    i0.ɵɵadvance(3);
    i0.ɵɵrepeater(row_r9);
  }
}
function NzDescriptionsComponent_Conditional_5_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵrepeaterCreate(0, NzDescriptionsComponent_Conditional_5_Conditional_1_For_1_Template, 6, 0, null, null, i0.ɵɵrepeaterTrackByIdentity);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵrepeater(ctx_r0.itemMatrix);
  }
}
function NzDescriptionsComponent_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, NzDescriptionsComponent_Conditional_5_Conditional_0_Template, 2, 0)(1, NzDescriptionsComponent_Conditional_5_Conditional_1_Template, 2, 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵconditional(!ctx_r0.nzBordered ? 0 : 1);
  }
}
let NzDescriptionsItemComponent = /*#__PURE__*/(() => {
  class NzDescriptionsItemComponent {
    content;
    nzSpan = 1;
    nzTitle = '';
    inputChange$ = new Subject();
    ngOnChanges() {
      this.inputChange$.next();
    }
    ngOnDestroy() {
      this.inputChange$.complete();
    }
    static ɵfac = function NzDescriptionsItemComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzDescriptionsItemComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzDescriptionsItemComponent,
      selectors: [["nz-descriptions-item"]],
      viewQuery: function NzDescriptionsItemComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(TemplateRef, 7);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.content = _t.first);
        }
      },
      inputs: {
        nzSpan: [2, "nzSpan", "nzSpan", numberAttribute],
        nzTitle: "nzTitle"
      },
      exportAs: ["nzDescriptionsItem"],
      features: [i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c0,
      decls: 1,
      vars: 0,
      template: function NzDescriptionsItemComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵtemplate(0, NzDescriptionsItemComponent_ng_template_0_Template, 1, 0, "ng-template");
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzDescriptionsItemComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const NZ_CONFIG_MODULE_NAME = 'descriptions';
const defaultColumnMap = {
  xxl: 3,
  xl: 3,
  lg: 3,
  md: 3,
  sm: 2,
  xs: 1
};
let NzDescriptionsComponent = (() => {
  let _nzBordered_decorators;
  let _nzBordered_initializers = [];
  let _nzBordered_extraInitializers = [];
  let _nzColumn_decorators;
  let _nzColumn_initializers = [];
  let _nzColumn_extraInitializers = [];
  let _nzSize_decorators;
  let _nzSize_initializers = [];
  let _nzSize_extraInitializers = [];
  let _nzColon_decorators;
  let _nzColon_initializers = [];
  let _nzColon_extraInitializers = [];
  return class NzDescriptionsComponent {
    static {
      const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
      _nzBordered_decorators = [WithConfig()];
      _nzColumn_decorators = [WithConfig()];
      _nzSize_decorators = [WithConfig()];
      _nzColon_decorators = [WithConfig()];
      __esDecorate(null, null, _nzBordered_decorators, {
        kind: "field",
        name: "nzBordered",
        static: false,
        private: false,
        access: {
          has: obj => "nzBordered" in obj,
          get: obj => obj.nzBordered,
          set: (obj, value) => {
            obj.nzBordered = value;
          }
        },
        metadata: _metadata
      }, _nzBordered_initializers, _nzBordered_extraInitializers);
      __esDecorate(null, null, _nzColumn_decorators, {
        kind: "field",
        name: "nzColumn",
        static: false,
        private: false,
        access: {
          has: obj => "nzColumn" in obj,
          get: obj => obj.nzColumn,
          set: (obj, value) => {
            obj.nzColumn = value;
          }
        },
        metadata: _metadata
      }, _nzColumn_initializers, _nzColumn_extraInitializers);
      __esDecorate(null, null, _nzSize_decorators, {
        kind: "field",
        name: "nzSize",
        static: false,
        private: false,
        access: {
          has: obj => "nzSize" in obj,
          get: obj => obj.nzSize,
          set: (obj, value) => {
            obj.nzSize = value;
          }
        },
        metadata: _metadata
      }, _nzSize_initializers, _nzSize_extraInitializers);
      __esDecorate(null, null, _nzColon_decorators, {
        kind: "field",
        name: "nzColon",
        static: false,
        private: false,
        access: {
          has: obj => "nzColon" in obj,
          get: obj => obj.nzColon,
          set: (obj, value) => {
            obj.nzColon = value;
          }
        },
        metadata: _metadata
      }, _nzColon_initializers, _nzColon_extraInitializers);
      if (_metadata) Object.defineProperty(this, Symbol.metadata, {
        enumerable: true,
        configurable: true,
        writable: true,
        value: _metadata
      });
    }
    nzConfigService = inject(NzConfigService);
    cdr = inject(ChangeDetectorRef);
    breakpointService = inject(NzBreakpointService);
    directionality = inject(Directionality);
    destroyRef = inject(DestroyRef);
    _nzModuleName = NZ_CONFIG_MODULE_NAME;
    items;
    nzBordered = __runInitializers(this, _nzBordered_initializers, false);
    nzLayout = (__runInitializers(this, _nzBordered_extraInitializers), 'horizontal');
    nzColumn = __runInitializers(this, _nzColumn_initializers, defaultColumnMap);
    nzSize = (__runInitializers(this, _nzColumn_extraInitializers), __runInitializers(this, _nzSize_initializers, 'default'));
    nzTitle = (__runInitializers(this, _nzSize_extraInitializers), '');
    nzExtra;
    nzColon = __runInitializers(this, _nzColon_initializers, true);
    itemMatrix = (__runInitializers(this, _nzColon_extraInitializers), []);
    realColumn = 3;
    dir = 'ltr';
    breakpoint = NzBreakpointEnum.md;
    ngOnInit() {
      this.dir = this.directionality.value;
      this.directionality.change?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(direction => {
        this.dir = direction;
      });
    }
    ngOnChanges(changes) {
      if (changes.nzColumn) {
        this.prepareMatrix();
      }
    }
    ngAfterContentInit() {
      const contentChange$ = this.items.changes.pipe(startWith(this.items));
      merge(contentChange$, contentChange$.pipe(switchMap(() => merge(...this.items.map(i => i.inputChange$)).pipe(auditTime(16)))), this.breakpointService.subscribe(gridResponsiveMap).pipe(tap(bp => this.breakpoint = bp))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.prepareMatrix();
        this.cdr.markForCheck();
      });
    }
    /**
     * Prepare the render matrix according to description items' spans.
     */
    prepareMatrix() {
      if (!this.items) {
        return;
      }
      let currentRow = [];
      let width = 0;
      const column = this.realColumn = this.getColumn();
      const items = this.items.toArray();
      const length = items.length;
      const matrix = [];
      const flushRow = () => {
        matrix.push(currentRow);
        currentRow = [];
        width = 0;
      };
      for (let i = 0; i < length; i++) {
        const item = items[i];
        const {
          nzTitle: title,
          content,
          nzSpan: span
        } = item;
        width += span;
        // If the last item make the row's length exceeds `nzColumn`, the last
        // item should take all the space left. This logic is implemented in the template.
        // Warn user about that.
        if (width >= column) {
          if (width > column) {
            warn(`"nzColumn" is ${column} but we have row length ${width}`);
          }
          currentRow.push({
            title,
            content,
            span: column - (width - span)
          });
          flushRow();
        } else if (i === length - 1) {
          currentRow.push({
            title,
            content,
            span: column - (width - span)
          });
          flushRow();
        } else {
          currentRow.push({
            title,
            content,
            span
          });
        }
      }
      this.itemMatrix = matrix;
    }
    getColumn() {
      if (typeof this.nzColumn !== 'number') {
        return this.nzColumn[this.breakpoint];
      }
      return this.nzColumn;
    }
    static ɵfac = function NzDescriptionsComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzDescriptionsComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzDescriptionsComponent,
      selectors: [["nz-descriptions"]],
      contentQueries: function NzDescriptionsComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NzDescriptionsItemComponent, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.items = _t);
        }
      },
      hostAttrs: [1, "ant-descriptions"],
      hostVars: 8,
      hostBindings: function NzDescriptionsComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("ant-descriptions-bordered", ctx.nzBordered)("ant-descriptions-middle", ctx.nzSize === "middle")("ant-descriptions-small", ctx.nzSize === "small")("ant-descriptions-rtl", ctx.dir === "rtl");
        }
      },
      inputs: {
        nzBordered: [2, "nzBordered", "nzBordered", booleanAttribute],
        nzLayout: "nzLayout",
        nzColumn: "nzColumn",
        nzSize: "nzSize",
        nzTitle: "nzTitle",
        nzExtra: "nzExtra",
        nzColon: [2, "nzColon", "nzColon", booleanAttribute]
      },
      exportAs: ["nzDescriptions"],
      features: [i0.ɵɵNgOnChangesFeature],
      decls: 6,
      vars: 3,
      consts: [[1, "ant-descriptions-header"], [1, "ant-descriptions-view"], [1, "ant-descriptions-title"], [1, "ant-descriptions-extra"], [4, "nzStringTemplateOutlet"], [1, "ant-descriptions-row"], [1, "ant-descriptions-item", 3, "colSpan"], [1, "ant-descriptions-item-container"], [1, "ant-descriptions-item-label"], [1, "ant-descriptions-item-content"], [3, "ngTemplateOutlet"], [1, "ant-descriptions-item-content", 3, "colSpan"], [1, "ant-descriptions-item-label", 3, "colSpan"]],
      template: function NzDescriptionsComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵconditionalCreate(0, NzDescriptionsComponent_Conditional_0_Template, 3, 2, "div", 0);
          i0.ɵɵelementStart(1, "div", 1)(2, "table")(3, "tbody");
          i0.ɵɵconditionalCreate(4, NzDescriptionsComponent_Conditional_4_Template, 2, 0);
          i0.ɵɵconditionalCreate(5, NzDescriptionsComponent_Conditional_5_Template, 2, 1);
          i0.ɵɵelementEnd()()();
        }
        if (rf & 2) {
          i0.ɵɵconditional(ctx.nzTitle || ctx.nzExtra ? 0 : -1);
          i0.ɵɵadvance(4);
          i0.ɵɵconditional(ctx.nzLayout === "horizontal" ? 4 : -1);
          i0.ɵɵadvance();
          i0.ɵɵconditional(ctx.nzLayout === "vertical" ? 5 : -1);
        }
      },
      dependencies: [NzOutletModule, i1.NzStringTemplateOutletDirective, NgTemplateOutlet],
      encapsulation: 2,
      changeDetection: 0
    });
  };
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzDescriptionsModule = /*#__PURE__*/(() => {
  class NzDescriptionsModule {
    static ɵfac = function NzDescriptionsModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzDescriptionsModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NzDescriptionsModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [NzDescriptionsComponent]
    });
  }
  return NzDescriptionsModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NzDescriptionsComponent, NzDescriptionsItemComponent, NzDescriptionsModule };
